import {
  CORRELATION_ATTRIBUTES,
  OBSERVABILITY_EDGE_PATHS,
  type ObservabilityMetadata,
} from "@clientportal/core/observability";
import { type BrowserOptions } from "@sentry/browser";
import { browserTracingIntegration, init, type Event } from "@sentry/react";

import {
  DEPLOYMENT_ENVIRONMENT,
  SERVICE_VERSION,
  getObservabilityContext,
  type ObservabilityContext,
} from "./correlation";

const dsn = import.meta.env.VITE_SENTRY_DSN;

interface SentryOptionsInput extends ObservabilityMetadata {
  origin?: string;
  dsn: string;
}

const createObservabilityTags = (
  tags: Event["tags"],
  context: ObservabilityContext,
  metadata: ObservabilityMetadata,
) => {
  const nextTags = { ...tags };
  // An expired action ID must not leak from a previously enriched event.
  delete nextTags[CORRELATION_ATTRIBUTES.eventId];

  return {
    ...nextTags,
    ...(context.sessionId ? { [CORRELATION_ATTRIBUTES.sessionId]: context.sessionId } : {}),
    ...(context.eventId ? { [CORRELATION_ATTRIBUTES.eventId]: context.eventId } : {}),
    ...(metadata.serviceVersion ? { "service.version": metadata.serviceVersion } : {}),
    ...(metadata.deploymentEnvironment ? { "deployment.environment": metadata.deploymentEnvironment } : {}),
  };
};

const createPostHogContext = (context: ObservabilityContext) => ({
  ...(context.sessionId ? { session_id: context.sessionId } : {}),
  ...(context.replayUrl ? { replay_url: context.replayUrl } : {}),
});

export const enrichSentryEvent = <TEvent extends Event>(
  event: TEvent,
  context: ObservabilityContext,
  metadata: ObservabilityMetadata,
): TEvent =>
  ({
    ...event,
    tags: createObservabilityTags(event.tags, context, metadata),
    contexts: {
      ...event.contexts,
      posthog: createPostHogContext(context),
    },
  }) as TEvent;

export const createSentryOptions = ({
  origin = window.location.origin,
  dsn,
  serviceVersion = SERVICE_VERSION,
  deploymentEnvironment = DEPLOYMENT_ENVIRONMENT,
}: SentryOptionsInput): BrowserOptions => ({
  environment: deploymentEnvironment,
  release: serviceVersion,
  dsn,
  tunnel: `${origin}${OBSERVABILITY_EDGE_PATHS.sentry}`,
  integrations: [browserTracingIntegration()],
  tracesSampleRate: 1.0,
  beforeSend: (event) =>
    enrichSentryEvent(event, getObservabilityContext(), {
      serviceVersion,
      deploymentEnvironment,
    }),
});

export const initSentry = () => {
  if (DEPLOYMENT_ENVIRONMENT && dsn) {
    init(createSentryOptions({ dsn }));
  }
};
